import React, { useCallback, useEffect, useState } from 'react'
import { Pressable, StyleSheet, Text, View } from 'react-native'
import {
  BG_STYLES,
  COMMON_STYLES,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import { rnNavigation } from 'app/utils/appConfig'
import RequestTypeChevronIcon from 'app/icons/RequestTypeChevronIcon'
import { useTranslation } from 'react-i18next'
import fetchRequestTypes from 'app/apis/fetchRequestTypes'
import fetchUserRole from 'app/apis/fetchUserRole'
import RoleSelection from 'app/components/priceRequest/RoleSelection'
import {
  ROLE_TYPES,
  RoleType,
  setUserRole,
} from 'app/store/reducer/requestflow'
import { REQUEST_TYPE_CONFIG } from 'app/utils/requestTypeConfig'
import ToastAndroid from 'app/mocks/ToastAndroid'
import { useDispatch } from 'react-redux'

const isRoleType = (value: string): value is RoleType =>
  ROLE_TYPES.includes(value as RoleType)

const RequestType = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [requestTypes, setRequestTypes] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [showNoRole, setShowNoRole] = useState(false)
  const [showRoleSelection, setShowRoleSelection] = useState(false)
  const [availableRoles, setAvailableRoles] = useState<RoleType[]>([])

  const loadRequestTypes = useCallback(
    async (role: RoleType) => {
      setLoading(true)
      const response = await fetchRequestTypes(role)

      if (!response) {
        ToastAndroid.show(t('unknown_error'), ToastAndroid.SHORT)
        setRequestTypes([])
      } else {
        const types = response.request_type ?? []
        const unmapped = types.filter(type => !REQUEST_TYPE_CONFIG[type])
        if (unmapped.length > 0 && __DEV__) {
          console.warn('Unmapped request types from API:', unmapped)
        }
        setRequestTypes(types)
      }

      setLoading(false)
    },
    [t],
  )

  const handleRoleSelect = useCallback(
    (selectedRole: RoleType) => {
      dispatch(setUserRole(selectedRole))
      localStorage.setItem('UserRole', selectedRole)
      setShowRoleSelection(false)
      loadRequestTypes(selectedRole)
    },
    [dispatch, loadRequestTypes],
  )

  useEffect(() => {
    let isMounted = true

    const initialize = async () => {
      setLoading(true)
      setShowNoRole(false)

      const savedRole = localStorage.getItem('UserRole')
      if (savedRole && isRoleType(savedRole)) {
        dispatch(setUserRole(savedRole))
        if (isMounted) {
          await loadRequestTypes(savedRole)
        }
        return
      }

      try {
        const apiResponse = await fetchUserRole()
        if (!isMounted) return

        if (!apiResponse) {
          ToastAndroid.show(t('unknown_error'), ToastAndroid.SHORT)
          setLoading(false)
          return
        }

        if (apiResponse.role_type.length === 0) {
          setShowNoRole(true)
          setLoading(false)
          return
        }

        if (apiResponse.role_type.length > 1) {
          setAvailableRoles(apiResponse.role_type)
          setShowRoleSelection(true)
          setLoading(false)
          return
        }

        const singleRole = apiResponse.role_type[0]
        dispatch(setUserRole(singleRole))
        localStorage.setItem('UserRole', singleRole)
        await loadRequestTypes(singleRole)
      } catch {
        if (isMounted) {
          ToastAndroid.show(t('unknown_error'), ToastAndroid.SHORT)
          setLoading(false)
        }
      }
    }

    initialize()

    return () => {
      isMounted = false
    }
  }, [dispatch, loadRequestTypes, t])

  if (loading) {
    return <Spinner />
  }

  const visibleRequestTypes = requestTypes.filter(
    type => REQUEST_TYPE_CONFIG[type],
  )
  const hasUnmappedTypes =
    requestTypes.length > 0 && visibleRequestTypes.length === 0

  return (
    <View style={[COMMON_STYLES.flex1, BG_STYLES.bgNeutral10, styles.container]}>
      <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.BASE, styles.title]}>
        {t('request_type_select_title')}
      </Text>

      {showNoRole ? (
        <Text
          style={[
            TYPOGRAPHY.TEXT2,
            VARIANTS_STYLES.BASE,
            TEXT_STYLES.textNeutral70,
            styles.emptyState,
          ]}
        >
          {t('demo_no_role_available')}
        </Text>
      ) : hasUnmappedTypes ? (
        <Text
          style={[
            TYPOGRAPHY.TEXT2,
            VARIANTS_STYLES.BASE,
            TEXT_STYLES.textNeutral70,
            styles.emptyState,
          ]}
        >
          {t('request_type_unrecognized_options')}
        </Text>
      ) : visibleRequestTypes.length === 0 ? (
        <Text
          style={[
            TYPOGRAPHY.TEXT2,
            VARIANTS_STYLES.BASE,
            TEXT_STYLES.textNeutral70,
            styles.emptyState,
          ]}
        >
          {t('request_type_no_options')}
        </Text>
      ) : (
        visibleRequestTypes.map(type => {
          const config = REQUEST_TYPE_CONFIG[type]
          const Icon = config.Icon

          return (
            <Pressable
              key={type}
              style={[styles.card, COMMON_STYLES.row, COMMON_STYLES.alignItemsCenter]}
              onPress={() => rnNavigation({ name: config.route })}
            >
              <View style={[styles.iconWrap, { backgroundColor: config.iconBg }]}>
                <Icon />
              </View>
              <Text
                style={[
                  COMMON_STYLES.flex1,
                  TYPOGRAPHY.HEADING3,
                  VARIANTS_STYLES.SEMI_EMPHASIZED,
                  TEXT_STYLES.textNeutral90,
                ]}
              >
                {t(config.labelKey)}
              </Text>
              <RequestTypeChevronIcon style={styles.chevron} />
            </Pressable>
          )
        })
      )}

      <RoleSelection
        visible={showRoleSelection}
        roles={availableRoles}
        onSelect={handleRoleSelect}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  title: {
    marginBottom: 14,
    letterSpacing: -0.25,
  },
  emptyState: {
    marginTop: 8,
  },
  card: {
    ...BG_STYLES.bgWhite,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#d9e0e8',
    padding: 10,
    marginBottom: 12,
    gap: 12,
    shadowColor: '#18274B',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 2,
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 8,
    ...COMMON_STYLES.alignItemsCenter,
    ...COMMON_STYLES.justifyCenter,
  },
  chevron: {
    transform: [{ rotate: '90deg' }],
  },
})

export default RequestType
